/**
 * A section of a form: a stack of fields, or a grid when the entity repeats.
 *
 * The grid is the reason repeating entities are a first-class idea rather than
 * a naming convention. Ten locations stacked as ten cards is a form nobody
 * finishes; ten locations as ten rows is a spreadsheet, which is what the
 * person filling it in already has open in another window.
 */
import { blankRow, evaluate, type AnswerDocument, type CompiledSection } from "@dp/shared";
import { Control } from "./Control.tsx";
import { Field } from "./Field.tsx";

export interface Problems {
  /** Keyed by the dotted answer path, e.g. `location.0.year_built`. */
  byPath: Map<string, string[]>;
}

export function Section({
  section, answers, readOnly, problems, onChange,
}: {
  section: CompiledSection;
  answers: AnswerDocument;
  readOnly: boolean;
  problems: Problems;
  onChange: (next: AnswerDocument) => void;
}) {
  const applies = evaluate(section.visibleWhen, { root: answers });
  if (!applies) return null;

  return (
    <div className="card">
      <div className="card-head">
        <div className="stack-sm">
          <h2>{section.label}</h2>
          {section.description && <p className="small muted">{section.description}</p>}
        </div>
        {section.repeating && (
          <span className="small muted">
            {rowsOf(answers, section).length} {rowsOf(answers, section).length === 1 ? "entry" : "entries"}
          </span>
        )}
      </div>

      <div className="card-body">
        {section.repeating
          ? <Grid section={section} answers={answers} readOnly={readOnly} problems={problems} onChange={onChange} />
          : <Flat section={section} answers={answers} readOnly={readOnly} problems={problems} onChange={onChange} />}
      </div>
    </div>
  );
}

function rowsOf(answers: AnswerDocument, section: CompiledSection): Record<string, unknown>[] {
  const value = answers[section.path];
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
}

function Flat({
  section, answers, readOnly, problems, onChange,
}: {
  section: CompiledSection;
  answers: AnswerDocument;
  readOnly: boolean;
  problems: Problems;
  onChange: (next: AnswerDocument) => void;
}) {
  const row = (answers[section.path] ?? {}) as Record<string, unknown>;

  function set(name: string, value: unknown) {
    onChange({ ...answers, [section.path]: { ...row, [name]: value } });
  }

  return (
    <div className="field-row">
      {section.fields.map((field) => (
        <Field
          key={field.key}
          field={field}
          value={row[field.name]}
          visible={evaluate(field.visibleWhen, { root: answers, scopeEntityKey: section.path, scopeRow: row })}
          readOnly={readOnly}
          problems={problems.byPath.get(`${section.path}.${field.name}`) ?? []}
          onChange={(value) => set(field.name, value)}
        />
      ))}
    </div>
  );
}

function Grid({
  section, answers, readOnly, problems, onChange,
}: {
  section: CompiledSection;
  answers: AnswerDocument;
  readOnly: boolean;
  problems: Problems;
  onChange: (next: AnswerDocument) => void;
}) {
  const rows = rowsOf(answers, section);

  function setRows(next: Record<string, unknown>[]) {
    onChange({ ...answers, [section.path]: next });
  }

  function setCell(index: number, name: string, value: unknown) {
    setRows(rows.map((row, position) => (position === index ? { ...row, [name]: value } : row)));
  }

  const sectionProblem = problems.byPath.get(section.path) ?? [];

  if (rows.length === 0) {
    return (
      <div className="stack-sm">
        {sectionProblem.map((problem) => <div className="notice notice-bad small" key={problem}>{problem}</div>)}
        <div className="empty small">
          Nothing added yet.
          {!readOnly && (
            <div style={{ marginTop: 10 }}>
              <button type="button" className="btn btn-sm" onClick={() => setRows([blankRow(section)])}>
                Add the first one
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="stack-sm">
      {sectionProblem.map((problem) => <div className="notice notice-bad small" key={problem}>{problem}</div>)}

      <div className="table-wrap">
        <table className="table grid-table">
          <thead>
            <tr>
              <th className="grid-number">#</th>
              {section.fields.map((field) => (
                <th key={field.key}>
                  {field.label}
                  {field.required && <span className="required-mark">*</span>}
                </th>
              ))}
              {!readOnly && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td className="grid-number">{index + 1}</td>
                {section.fields.map((field) => {
                  const path = `${section.path}.${index}.${field.name}`;
                  const cellProblems = problems.byPath.get(path) ?? [];
                  const visible = evaluate(field.visibleWhen, {
                    root: answers, scopeEntityKey: section.path, scopeRow: row,
                  });
                  return (
                    <td key={field.key} className={visible ? undefined : "is-not-asked"}>
                      <Control
                        field={field}
                        value={row[field.name]}
                        disabled={!visible || readOnly || field.readOnly}
                        wrong={visible && cellProblems.length > 0}
                        onChange={(value) => setCell(index, field.name, value)}
                      />
                      {visible && cellProblems.map((problem) => (
                        <div className="problem" key={problem}>{problem}</div>
                      ))}
                    </td>
                  );
                })}
                {!readOnly && (
                  <td className="actions">
                    <button
                      type="button" className="btn btn-sm btn-ghost" aria-label={`Remove entry ${index + 1}`}
                      onClick={() => setRows(rows.filter((_, position) => position !== index))}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <button type="button" className="btn btn-sm" onClick={() => setRows([...rows, blankRow(section)])}>
          Add another
        </button>
      )}
    </div>
  );
}
